import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { getFileSizeStringValue } from '../../../../models/UiUtils';
import { Button } from '../../../components-v2/Button';
import { isRedrawable } from '../../../components-v2/UnifiedImageDialog/redrawableMedia';
import { CheckIcon } from '../../svgs/icons/CheckIcon';
import { CropIcon } from '../../svgs/icons/CropIcon';
import { EnlargeIcon } from '../../svgs/icons/EnlargeIcon';
import { GripVerticalIcon } from '../../svgs/icons/GripVerticalIcon';
import { SparklesIcon } from '../../svgs/icons/SparklesIcon';
import { TrashIcon } from '../../svgs/icons/TrashIcon';
import { REVEAL_CLASS, styleSheet } from './styles';

const isVideoAttachment = (attachment: Api.IFileAttachment) =>
	!!attachment.mimeType?.toLowerCase().startsWith('video/');

type DragHandlers = Pick<
	React.HTMLAttributes<HTMLLIElement>,
	'onDragEnd' | 'onDragOver' | 'onDragStart' | 'onDrop' | 'onKeyDown'
>;

export interface IMediaTileProps extends DragHandlers {
	attachment: Api.IFileAttachment;
	draggable?: boolean;
	/** Set while the AI edit is unavailable; the text says why. */
	editWithAIDisabledReason?: string;
	isDragging: boolean;
	/** Dropping now would land the dragged tile at this tile's position. */
	isDropTarget: boolean;
	/** Only meaningful while `routingPlatform` is set. */
	isRouted: boolean;
	onCrop?: (attachment: Api.IFileAttachment) => void;
	onEditWithAI?: (attachment: Api.IFileAttachment) => void;
	onEnlarge?: (attachment: Api.IFileAttachment) => void;
	onRemove?: (attachment: Api.IFileAttachment) => void;
	onToggleRouted?: (attachment: Api.IFileAttachment) => void;
	readOnly?: boolean;
	/** The version on screen, when the post is split. Unset means one post for every platform. */
	routingPlatform?: Api.SocialMediaType;
}

// One label whatever the state, since aria-pressed carries it: a label that also flipped would be read
// as "Remove…, pressed".
const describeRouting = (attachment: Api.IFileAttachment, platform: Api.SocialMediaType) =>
	`Include ${attachment.fileName} in the ${platform} version`;

const TileAction: React.FC<{
	/** Set to turn the action off; the text says why. */
	disabledReason?: string;
	icon: React.ReactNode;
	isDestructive?: boolean;
	label: string;
	onClick: () => void;
	title: string;
}> = ({ disabledReason, icon, isDestructive, label, onClick, title }) => {
	const button = (
		<Button
			aria-label={label}
			disabled={!!disabledReason}
			onClick={onClick}
			size='icon'
			styles={[styleSheet.tileAction, ...(isDestructive ? [styleSheet.tileActionDestructive] : [])]}
			title={title}
			type='button'
			variant='ghost'
		>
			{icon}
		</Button>
	);
	// A disabled button takes no pointer events, so its own title never surfaces, and the bar holding
	// it takes none either. The wrapper carries the reason and the hover that reveals it.
	return disabledReason ? (
		<span className={css(styleSheet.tileActionDisabledWrap)} title={disabledReason}>
			{button}
		</span>
	) : (
		button
	);
};

const TileActions: React.FC<{
	attachment: Api.IFileAttachment;
	/** Set while the AI edit is unavailable; the text says why. */
	editWithAIDisabledReason?: string;
	onCrop?: (attachment: Api.IFileAttachment) => void;
	onEditWithAI?: (attachment: Api.IFileAttachment) => void;
	onEnlarge?: (attachment: Api.IFileAttachment) => void;
	onRemove?: (attachment: Api.IFileAttachment) => void;
}> = ({ attachment, editWithAIDisabledReason, onCrop, onEditWithAI, onEnlarge, onRemove }) => (
	<div className={`${REVEAL_CLASS} ${css(styleSheet.tileActions)}`}>
		{onEnlarge ? (
			<TileAction
				icon={<EnlargeIcon fill='currentColor' height={18} width={18} />}
				label={`Enlarge ${attachment.fileName}`}
				onClick={() => onEnlarge(attachment)}
				title='Enlarge'
			/>
		) : null}
		{onCrop ? (
			<TileAction
				icon={<CropIcon fill='currentColor' height={18} width={18} />}
				label={`Crop ${attachment.fileName}`}
				onClick={() => onCrop(attachment)}
				title='Crop'
			/>
		) : null}
		{onEditWithAI ? (
			<TileAction
				disabledReason={editWithAIDisabledReason}
				icon={<SparklesIcon height={18} width={18} />}
				label={`Edit ${attachment.fileName} with AI`}
				onClick={() => onEditWithAI(attachment)}
				title='Edit with AI'
			/>
		) : null}
		{onRemove ? (
			<TileAction
				icon={<TrashIcon fillColor='currentColor' height={18} width={18} />}
				isDestructive
				label={`Remove ${attachment.fileName} from this post`}
				onClick={() => onRemove(attachment)}
				title='Remove'
			/>
		) : null}
	</div>
);

/**
 * One piece of the post's media, carrying everything that can be done to it: reorder by dragging
 * the tile, and — revealed on hover or focus — enlarge, edit, and remove.
 *
 * While the post is split the tile also routes. An image's thumbnail is the routing toggle, since
 * it is the largest target on the tile, and unrouted media dims instead of disappearing so the
 * whole pool stays reachable from every platform tab. A video plays in place, so its own controls
 * own its clicks and the corner checkbox does its routing instead.
 */
export const MediaTile: React.FC<IMediaTileProps> = ({
	attachment,
	draggable,
	editWithAIDisabledReason,
	isDragging,
	isDropTarget,
	isRouted,
	onCrop,
	onDragEnd,
	onDragOver,
	onDragStart,
	onDrop,
	onEditWithAI,
	onEnlarge,
	onKeyDown,
	onRemove,
	onToggleRouted,
	readOnly,
	routingPlatform,
}) => {
	const isVideo = isVideoAttachment(attachment);
	const redrawable = isRedrawable(attachment);
	const routesFromThumbnail = !!routingPlatform && !readOnly && !isVideo;
	const enlargesFromThumbnail = !routesFromThumbnail && !isVideo && !!onEnlarge;

	const media = isVideo ? (
		<video className={css(styleSheet.tileVideo)} controls preload='metadata' src={attachment.url} />
	) : (
		// An image is a drag source of its own by default, which would drag the picture instead of
		// reordering the tile that holds it.
		<img alt='' className={css(styleSheet.tileImage)} draggable={false} src={attachment.url} />
	);

	const thumbnailAction = routesFromThumbnail
		? { label: describeRouting(attachment, routingPlatform), press: () => onToggleRouted?.(attachment) }
		: enlargesFromThumbnail
			? { label: `Enlarge ${attachment.fileName}`, press: () => onEnlarge(attachment) }
			: null;

	return (
		<li
			className={css(
				styleSheet.tile,
				isVideo && styleSheet.tileWide,
				draggable && styleSheet.tileDraggable,
				!!routingPlatform && isRouted && styleSheet.tileRouted,
				isDropTarget && styleSheet.tileDropTarget,
				isDragging && styleSheet.tileDragging
			)}
			draggable={draggable}
			onDragEnd={onDragEnd}
			onDragOver={onDragOver}
			onDragStart={onDragStart}
			onDrop={onDrop}
			onKeyDown={onKeyDown}
			title={`${attachment.fileName} · ${getFileSizeStringValue(attachment.fileSize)}`}
		>
			{thumbnailAction ? (
				<Button
					aria-label={thumbnailAction.label}
					aria-pressed={routesFromThumbnail ? isRouted : undefined}
					onClick={thumbnailAction.press}
					styles={[styleSheet.tileBody]}
					type='button'
					variant='ghost'
				>
					{media}
				</Button>
			) : (
				media
			)}
			{routingPlatform ? (
				<>
					<span aria-hidden='true' className={css(styleSheet.tileVeil, isRouted && styleSheet.tileVeilRouted)} />
					{routesFromThumbnail ? (
						<span aria-hidden='true' className={css(styleSheet.tileCheck, isRouted && styleSheet.tileCheckRouted)}>
							{isRouted ? <CheckIcon className={css(styleSheet.tileCheckIcon)} /> : null}
						</span>
					) : (
						<Button
							aria-label={describeRouting(attachment, routingPlatform)}
							aria-pressed={isRouted}
							disabled={readOnly}
							onClick={() => onToggleRouted?.(attachment)}
							styles={[
								styleSheet.tileCheck,
								styleSheet.tileCheckButton,
								...(isRouted ? [styleSheet.tileCheckRouted] : []),
							]}
							type='button'
							variant='ghost'
						>
							{isRouted ? <CheckIcon className={css(styleSheet.tileCheckIcon)} /> : null}
						</Button>
					)}
				</>
			) : null}
			{isVideo ? <span className={css(styleSheet.tileBadge)}>Video</span> : null}
			{draggable ? (
				<span aria-hidden='true' className={css(styleSheet.tileDragHandle)} data-media-drag-handle='true'>
					<GripVerticalIcon className={css(styleSheet.tileDragHandleIcon)} />
				</span>
			) : null}
			{enlargesFromThumbnail ? (
				<span aria-hidden='true' className={`${REVEAL_CLASS} ${css(styleSheet.tileZoomCue)}`}>
					<EnlargeIcon fill='currentColor' height={18} width={18} />
				</span>
			) : null}
			{onCrop || onEditWithAI || onEnlarge || onRemove ? (
				<TileActions
					attachment={attachment}
					// A crop is a canvas redraw of one picture, so unlike the AI edit it stays available
					// while the post is customized: the caller swaps the replacement in by id across versions.
					onCrop={onCrop && redrawable ? onCrop : undefined}
					editWithAIDisabledReason={editWithAIDisabledReason}
					onEditWithAI={onEditWithAI && redrawable ? onEditWithAI : undefined}
					// Skipped when the thumbnail is already the enlarge target, so no tile carries the
					// same action twice.
					onEnlarge={onEnlarge && routesFromThumbnail ? onEnlarge : undefined}
					onRemove={onRemove}
				/>
			) : null}
		</li>
	);
};
